import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  accepted: boolean;
  onAcceptChange: (accepted: boolean) => void;
}

const TermsModal: React.FC<TermsModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  accepted,
  onAcceptChange
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Scroll page to top when modal opens
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
      
      // Add class to body to lower navbar z-index
      document.body.classList.add('terms-modal-open');
      
      // Also scroll modal content to top
      if (modalRef.current) {
        const scrollContainer = modalRef.current.querySelector('.terms-modal-scroll');
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
      }
    } else {
      // Restore body scrolling when modal closes
      document.body.style.overflow = '';
      // Remove class from body
      document.body.classList.remove('terms-modal-open');
    }

    // Cleanup: restore body scrolling and remove class on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('terms-modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  };

  const contentStyle: React.CSSProperties = {
    position: 'relative',
    backgroundColor: '#fff',
    borderRadius: '8px',
    maxWidth: '600px',
    maxHeight: '90vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  };

  const scrollStyle: React.CSSProperties = {
    overflowY: 'auto',
    flex: 1,
    padding: '1.5rem',
  };

  const footerStyle: React.CSSProperties = {
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  };

  const closeStyle: React.CSSProperties = {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
  };

  return createPortal(
    <div className="terms-modal-overlay" style={overlayStyle} onClick={onClose}>
      <div className="terms-modal-content" ref={modalRef} style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <button className="terms-modal-close" style={closeStyle} onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="terms-modal-scroll" style={scrollStyle}>
          <div className="terms-modal-text">
            <h1>Privacy Policy</h1>
            <p className="terms-last-updated">Last Updated: January 2025</p>
            
            <p>
              At Cognidaily, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>

            <h2>1. Information We Collect</h2>
            <h3>1.1 Account Information</h3>
            <p>
              When you create an account, we collect information such as your email address, username, and password. This information is necessary to create and manage your account.
            </p>

            <h3>1.2 Quiz and Performance Data</h3>
            <p>
              We collect data related to your quiz participation, including:
            </p>
            <ul>
              <li>Quiz scores and accuracy</li>
              <li>Response times</li>
              <li>Questions answered correctly or incorrectly</li>
              <li>Leaderboard rankings</li>
              <li>Badge achievements and XP progress</li>
            </ul>

            <h3>1.3 Usage Data</h3>
            <p>
              We automatically collect certain information when you use our platform, including:
            </p>
            <ul>
              <li>Device information (browser type, operating system)</li>
              <li>IP address and general location data</li>
              <li>Pages visited and features used</li>
              <li>Time spent on the platform</li>
              <li>Referral sources</li>
            </ul>

            <h3>1.4 Cookies and Tracking Technologies</h3>
            <p>
              We use cookies and similar tracking technologies to enhance your experience. For detailed information about our use of cookies, please see our Cookie Policy.
            </p>

            <h2>2. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            
            <h3>2.1 Service Provision</h3>
            <ul>
              <li>To provide and maintain our quiz platform</li>
              <li>To process your quiz attempts and calculate scores</li>
              <li>To display leaderboards and rankings</li>
              <li>To track your progress, XP, and achievements</li>
            </ul>

            <h3>2.2 Personalization</h3>
            <ul>
              <li>To customize your experience on the platform</li>
              <li>To provide relevant content and features</li>
              <li>To improve our question selection and difficulty</li>
            </ul>

            <h3>2.3 Communication</h3>
            <ul>
              <li>To send you important updates about the service</li>
              <li>To respond to your inquiries and support requests</li>
              <li>To send notifications about your achievements and progress</li>
            </ul>

            <h3>2.4 Analytics and Improvement</h3>
            <ul>
              <li>To analyze platform usage and performance</li>
              <li>To identify and fix technical issues</li>
              <li>To develop new features and improve existing ones</li>
            </ul>

            <h3>2.5 Legal Compliance</h3>
            <ul>
              <li>To comply with applicable laws and regulations</li>
              <li>To enforce our Terms of Service</li>
              <li>To protect our rights and prevent fraud</li>
            </ul>

            <h2>3. Information Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

            <h3>3.1 Public Information</h3>
            <p>
              Certain information is publicly visible on leaderboards, including your username, scores, rankings, and achievements. This information is essential to the competitive nature of our platform.
            </p>

            <h3>3.2 Service Providers</h3>
            <p>
              We may share information with third-party service providers who assist us in operating our platform, such as hosting and cloud storage providers, analytics services, email delivery services, and payment processors (if applicable). These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
            </p>

            <h3>3.3 Legal Requirements</h3>
            <p>
              We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
            </p>

            <h3>3.4 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.
            </p>

            <h2>4. Your Rights and Choices</h2>
            <h3>4.1 Access and Correction</h3>
            <p>
              You can access and update your account information at any time through your profile settings.
            </p>

            <h3>4.2 Data Deletion</h3>
            <p>
              You may request deletion of your account and associated data by contacting us at hello@cognidaily.io. We will process your request in accordance with applicable law.
            </p>

            <h3>4.3 GDPR Rights (European Users)</h3>
            <p>
              If you are located in the European Economic Area, you have rights including access, rectification, erasure, restriction of processing, data portability, and objection to processing. To exercise these rights, please contact us at hello@cognidaily.io.
            </p>

            <h3>4.4 CCPA Rights (California Users)</h3>
            <p>
              If you are a California resident, you have the right to know what personal information we collect, know whether we sell or disclose your personal information, opt-out of the sale of personal information (we do not sell personal information), access and delete your personal information, and non-discrimination for exercising your privacy rights.
            </p>

            <h3>4.5 Cookie Preferences</h3>
            <p>
              You can manage your cookie preferences through your browser settings. For more information, see our Cookie Policy.
            </p>

            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption of data in transit using SSL/TLS, secure password storage using industry-standard hashing, regular security assessments and updates, access controls and authentication mechanisms, and secure hosting infrastructure. However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>

            <h2>6. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When you delete your account, we will delete or anonymize your personal information, except where we are required to retain it for legal purposes.
            </p>

            <h2>7. Children's Privacy</h2>
            <p>
              Our platform is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately, and we will take steps to delete such information.
            </p>

            <h2>8. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We take appropriate safeguards to ensure your information receives adequate protection in accordance with this Privacy Policy.
            </p>

            <h2>9. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of the platform after such changes constitutes your acceptance of the updated Privacy Policy.
            </p>

            <h2>10. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at hello@cognidaily.io.
            </p>

            <h1>Terms of Service</h1>
            <p className="terms-last-updated">Last Updated: January 2025</p>
            
            <p>
              Welcome to Cognidaily. These Terms of Service ("Terms") govern your access to and use of our platform. By accessing or using Cognidaily, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our platform.
            </p>

            <h2>1. Acceptance of Terms</h2>
            <p>
              By creating an account, accessing, or using Cognidaily, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. These Terms constitute a legally binding agreement between you and Cognidaily.
            </p>

            <h2>2. Account Registration and Responsibilities</h2>
            <h3>2.1 Account Creation</h3>
            <p>
              To use certain features of our platform, you must create an account. When creating an account, you agree to provide accurate, current, and complete information, maintain and update your information to keep it accurate, maintain the security of your account credentials, accept responsibility for all activities under your account, and notify us immediately of any unauthorized access.
            </p>

            <h3>2.2 Account Eligibility</h3>
            <p>
              You must be at least 13 years old to create an account. By creating an account, you represent that you meet this age requirement and have the legal capacity to enter into these Terms.
            </p>

            <h3>2.3 Account Termination</h3>
            <p>
              We reserve the right to suspend or terminate your account at any time, with or without notice, for violation of these Terms or for any other reason we deem necessary to protect the integrity of our platform.
            </p>

            <h2>3. Acceptable Use Policy</h2>
            <p>You agree to use Cognidaily only for lawful purposes and in accordance with these Terms. You agree not to attempt to cheat, manipulate, or exploit the quiz system; use automated tools, bots, scripts, or other means to gain unfair advantages; share answers or collaborate with others during competitive quizzes; impersonate any person or entity or misrepresent your identity; harass, threaten, or harm other users; post or transmit any content that is illegal, harmful, or offensive; violate any applicable laws or regulations; interfere with or disrupt the platform's operation; attempt to gain unauthorized access to our systems; or reverse engineer, decompile, or disassemble any part of the platform.</p>

            <h3>3.2 Fair Competition</h3>
            <p>
              Cognidaily is designed to provide fair and competitive quiz experiences. Any attempt to gain an unfair advantage, including but not limited to cheating, using external resources during timed quizzes, or manipulating scores, is strictly prohibited and may result in immediate account termination.
            </p>

            <h2>4. Intellectual Property Rights</h2>
            <h3>4.1 Our Content</h3>
            <p>
              All content on Cognidaily, including but not limited to text, graphics, logos, questions, answers, software, and code, is the property of Cognidaily or its licensors and is protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3>4.2 Limited License</h3>
            <p>
              We grant you a limited, non-exclusive, non-transferable, revocable license to access and use Cognidaily for personal, non-commercial purposes in accordance with these Terms. This license does not include the right to reproduce, distribute, or create derivative works; use our content for commercial purposes; remove any copyright or proprietary notices; or scrape, crawl, or extract data from the platform.
            </p>

            <h2>5. Quiz Rules and Scoring</h2>
            <h3>5.1 Quiz Participation</h3>
            <p>
              When participating in quizzes, you agree to answer questions independently without external assistance, complete quizzes within the designated time limits, accept the scoring system and results, and respect the competitive nature of the platform.
            </p>

            <h3>5.2 Score Calculation</h3>
            <p>
              Scores are calculated based on accuracy and speed. We reserve the right to adjust scores, remove invalid attempts, or recalculate rankings if we detect any irregularities or violations of these Terms.
            </p>

            <h3>5.3 Leaderboards</h3>
            <p>
              Leaderboard rankings are determined by quiz scores and are updated in real-time. We reserve the right to remove users from leaderboards or adjust rankings for violations of these Terms or technical issues.
            </p>

            <h2>6. Service Modifications and Availability</h2>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of Cognidaily at any time, with or without notice. We may also impose limits on certain features or restrict access to parts of the platform. While we strive to maintain continuous availability, we do not guarantee that Cognidaily will be available at all times.
            </p>

            <h2>7. Disclaimers</h2>
            <p>
              Cognidaily is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the platform will be uninterrupted, error-free, or free from viruses or other harmful components. While we strive to provide accurate quiz questions and content, we do not guarantee the accuracy, completeness, or reliability of any content on the platform.
            </p>

            <h2>8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Cognidaily and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or relating to your use of the platform. Our total liability for any claims arising from your use of Cognidaily shall not exceed the amount you paid to us (if any) in the twelve months preceding the claim, or $100, whichever is greater.
            </p>

            <h2>9. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Cognidaily and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of or relating to your use of the platform, your violation of these Terms, your violation of any rights of another party, or any content you submit to the platform.
            </p>

            <h2>10. Dispute Resolution</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of Australia, without regard to its conflict of law provisions. If you have any dispute with Cognidaily, you agree to first contact us at hello@cognidaily.io to attempt to resolve the dispute informally. If we cannot resolve the dispute within 60 days, you agree to submit to the exclusive jurisdiction of the courts located in Australia.
            </p>

            <h2>11. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of Cognidaily after such changes constitutes your acceptance of the updated Terms. If you do not agree to the modified Terms, you must stop using the platform and may delete your account.
            </p>

            <h2>12. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at hello@cognidaily.io.
            </p>

            <h1>Cookie Policy</h1>
            <p className="terms-last-updated">Last Updated: January 2025</p>
            
            <p>
              This Cookie Policy explains how Cognidaily uses cookies and similar tracking technologies when you visit our platform. It explains what these technologies are, why we use them, and your rights to control our use of them.
            </p>

            <h2>1. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are placed on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners. Cookies allow a website to recognize your device and store some information about your preferences or past actions. This helps us provide you with a better experience when you return to our platform.
            </p>

            <h2>2. How We Use Cookies</h2>
            <p>
              We use cookies and similar technologies for several purposes:
            </p>

            <h3>2.1 Essential Cookies</h3>
            <p>
              These cookies are necessary for the platform to function properly. They enable core functionality such as user authentication and session management, security features and fraud prevention, remembering your login status, maintaining your quiz progress, and CSRF protection. These cookies cannot be disabled in our systems as they are essential for the platform to work.
            </p>

            <h3>2.2 Functional Cookies</h3>
            <p>
              These cookies allow the platform to remember choices you make and provide enhanced, personalized features such as remembering your preferences and settings, theme preferences (light/dark mode), language preferences, and display preferences.
            </p>

            <h3>2.3 Analytics Cookies</h3>
            <p>
              These cookies help us understand how visitors interact with our platform by collecting and reporting information anonymously about page views and navigation patterns, time spent on pages, features used and interactions, and error messages and performance issues.
            </p>

            <h2>3. Types of Cookies We Use</h2>
            <h3>3.1 Session Cookies</h3>
            <p>
              Session cookies are temporary cookies that are deleted when you close your browser. We use session cookies to maintain your login session, track your quiz progress during a session, and store temporary preferences.
            </p>

            <h3>3.2 Persistent Cookies</h3>
            <p>
              Persistent cookies remain on your device for a set period or until you delete them. We use persistent cookies to remember your login credentials (if you choose "Remember Me"), store your theme and display preferences, and maintain your account settings.
            </p>

            <h3>3.3 First-Party Cookies</h3>
            <p>
              First-party cookies are set directly by Cognidaily. These cookies are used to provide core functionality and improve your experience on our platform.
            </p>

            <h3>3.4 Third-Party Cookies</h3>
            <p>
              Third-party cookies are set by domains other than Cognidaily. We may use third-party cookies for analytics services, performance monitoring, and error tracking. These third parties have their own privacy policies and cookie practices.
            </p>

            <h2>4. Other Tracking Technologies</h2>
            <p>
              In addition to cookies, we may use other tracking technologies including browser local storage to store information such as your preferences and settings, session storage for temporary data that is cleared when you close your browser tab, and web beacons (small invisible images) to track email open rates and understand how users interact with our communications.
            </p>

            <h2>5. Cookie Duration</h2>
            <p>
              Session cookies expire when you close your browser. Persistent cookies remain on your device for varying periods: authentication cookies typically expire after 30 days of inactivity, preference cookies may remain for up to 1 year, and analytics cookies typically expire after 2 years.
            </p>

            <h2>6. Managing Your Cookie Preferences</h2>
            <p>
              Most web browsers allow you to control cookies through their settings. You can block all cookies, block third-party cookies only, delete existing cookies, or set your browser to notify you before cookies are placed. Please note that disabling certain cookies may impact your experience on Cognidaily.
            </p>

            <h2>7. Third-Party Cookies</h2>
            <p>
              Some cookies on our platform are set by third-party services. We work with trusted partners who help us operate and improve Cognidaily. These third parties may set cookies to analyze platform usage and performance, monitor for errors and technical issues, and provide security features. We do not control these third-party cookies. Please refer to the privacy policies of these third parties for information about their cookie practices.
            </p>

            <h2>8. Updates to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Cookie Policy on this page and updating the "Last Updated" date.
            </p>

            <h2>9. More Information</h2>
            <p>
              For more information about how we handle your personal information, please see our Privacy Policy. If you have questions about our use of cookies, please contact us at hello@cognidaily.io.
            </p>

            <h1>Disclaimer</h1>
            <p className="terms-last-updated">Last Updated: January 2025</p>
            
            <p>
              Please read this Disclaimer carefully before using Cognidaily. By accessing or using our platform, you acknowledge that you have read, understood, and agree to be bound by the terms of this Disclaimer.
            </p>

            <h2>1. Educational and Entertainment Purpose</h2>
            <p>
              Cognidaily is designed for educational and entertainment purposes. The quizzes, questions, and content provided on our platform are intended to stimulate intellectual curiosity and learning, provide an engaging competitive experience, offer mental exercise and cognitive challenges, and foster friendly competition among users. Our platform is not intended to serve as a substitute for formal education, professional training, or academic assessment.
            </p>

            <h2>2. Content Accuracy and Reliability</h2>
            <h3>2.1 No Warranty of Accuracy</h3>
            <p>
              While we strive to provide accurate and reliable quiz questions and content, we make no representations or warranties regarding the accuracy, completeness, or reliability of quiz questions; the correctness of answers or explanations; the currency of information presented; or the suitability of content for any particular purpose.
            </p>

            <h3>2.2 Content Sources</h3>
            <p>
              Quiz questions and content may be derived from various sources, including publicly available information, educational materials, and general knowledge. Advanced technologies may be utilized in the curation and quality assurance of our question content. We do not guarantee that all information is current, accurate, or free from errors.
            </p>

            <h3>2.3 Subjectivity and Interpretation</h3>
            <p>
              Some questions may involve matters of interpretation, opinion, or evolving knowledge. In such cases, we provide answers based on generally accepted information at the time of question creation, but these may not reflect all perspectives or the most recent developments.
            </p>

            <h2>3. Quiz Results and Scores</h2>
            <h3>3.1 No Guarantee of Results</h3>
            <p>
              Your quiz scores, rankings, and achievements on Cognidaily are based on your performance within our platform's system. These results do not reflect your actual knowledge or intelligence, are not intended for use in academic or professional contexts, may be influenced by factors such as timing, question selection, and technical issues, and should not be used as a measure of educational achievement.
            </p>

            <h3>3.2 Scoring System</h3>
            <p>
              Our scoring system is designed to create fair competition but may not perfectly reflect knowledge or ability. Scores are calculated based on accuracy and speed, and we reserve the right to adjust scores or rankings for technical reasons or to maintain platform integrity.
            </p>

            <h3>3.3 Leaderboard Rankings</h3>
            <p>
              Leaderboard rankings are relative and based on performance within our platform. They do not represent objective measures of knowledge, intelligence, or educational achievement.
            </p>

            <h2>4. No Professional Advice</h2>
            <p>
              The content on Cognidaily does not constitute professional, legal, medical, financial, or any other form of professional advice. You should not rely on information from our platform for making important decisions, professional or academic purposes, medical, legal, or financial matters, or any situation requiring professional expertise. Always consult qualified professionals for advice on matters requiring specialized knowledge or expertise.
            </p>

            <h2>5. Platform Availability and Performance</h2>
            <p>
              We do not guarantee that Cognidaily will be available at all times or that it will be free from errors, interruptions, or technical issues. The platform may be unavailable due to scheduled maintenance, technical difficulties, server outages, or force majeure events. We are not responsible for any delays, failures, or performance issues that may affect your ability to complete quizzes or access features.
            </p>

            <h2>6. User Responsibility</h2>
            <p>
              You are encouraged to independently verify any information you encounter on Cognidaily, especially if you intend to use it for important purposes. We are not responsible for any decisions or actions you take based on information from our platform. You are responsible for using Cognidaily in an appropriate manner and in accordance with our Terms of Service. You are also responsible for ensuring that your device, browser, and internet connection meet the technical requirements necessary to use Cognidaily.
            </p>

            <h2>7. Third-Party Content and Links</h2>
            <p>
              Cognidaily may contain links to third-party websites or resources. We are not responsible for the content, accuracy, or availability of third-party sites; the privacy practices of third-party sites; any products, services, or information offered by third parties; or any damages or losses arising from your use of third-party sites. We may use third-party services to operate and improve Cognidaily. We are not responsible for the performance, availability, or practices of these third-party services.
            </p>

            <h2>8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Cognidaily and its officers, directors, employees, and agents shall not be liable for any errors or inaccuracies in quiz content; any loss or damage resulting from your use of the platform; any interruption or unavailability of the platform; any decisions made based on information from the platform; or any indirect, incidental, special, or consequential damages. Your use of Cognidaily is at your own risk. The platform is provided "as is" and "as available" without warranties of any kind.
            </p>

            <h2>9. Intellectual Property</h2>
            <p>
              All content on Cognidaily, including quiz questions, answers, explanations, and other materials, is protected by intellectual property laws. While we strive to respect the intellectual property rights of others, we cannot guarantee that all content is free from potential claims. If you believe that any content on our platform infringes your rights, please contact us.
            </p>

            <h2>10. Changes to Platform</h2>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of Cognidaily at any time, including quiz questions and content, scoring systems and algorithms, features and functionality, and platform availability. We are not obligated to provide notice of such changes, and we are not liable for any impact these changes may have on your experience or results.
            </p>

            <h2>11. Jurisdiction and Governing Law</h2>
            <p>
              This Disclaimer shall be governed by and construed in accordance with the laws of Australia. Any disputes arising from or relating to this Disclaimer or your use of Cognidaily shall be subject to the exclusive jurisdiction of the courts of Australia.
            </p>

            <h2>12. Updates to This Disclaimer</h2>
            <p>
              We may update this Disclaimer from time to time to reflect changes in our practices, the platform, or for other operational, legal, or regulatory reasons. We will notify you of material changes by posting the new Disclaimer on this page and updating the "Last Updated" date. Your continued use of Cognidaily after such changes constitutes your acceptance of the updated Disclaimer.
            </p>

            <h2>13. Contact Information</h2>
            <p>
              If you have any questions about this Disclaimer, please contact us at hello@cognidaily.io.
            </p>
          </div>
        </div>

        <div className="terms-modal-footer" style={footerStyle}>
          <label className="terms-checkbox-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => onAcceptChange(e.target.checked)}
              className="terms-checkbox"
            />
            <span>I Understand</span>
          </label>
          <button
            className="btn btn-primary"
            onClick={onAccept}
            disabled={!accepted}
          >
            Create Account
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TermsModal;






